import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { useCurrency } from '../../../../../context/CurrencyContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const inputClass =
  'h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none ring-brand-500/30 focus:border-brand-500 focus:ring-2';
const primaryBtnClass =
  'inline-flex items-center justify-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60';

type StripePayout = {
  id: string;
  stripePayoutId: string;
  currency: string;
  amountMinor: number;
  status: string;
  arrivalAt: string | null;
  description: string | null;
  bankLinks: { confirmationId: string }[];
};

type BankConfirmation = {
  id: string;
  currency: string;
  periodStart: string;
  periodEnd: string;
  bankAmountMinor: number;
  confirmedAt: string;
  note: string | null;
  payoutLinks: { payout: StripePayout }[];
};

export default function BankReconciliationPage() {
  const { t } = useTranslation();
  const { authorizedFetch } = useAuth();
  const { formatMajorInCurrency } = useCurrency();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [payouts, setPayouts] = useState<StripePayout[]>([]);
  const [confirmations, setConfirmations] = useState<BankConfirmation[]>([]);

  const [syncing, setSyncing] = useState(false);
  const [creating, setCreating] = useState(false);

  const [currency, setCurrency] = useState('USD');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [bankAmountMinor, setBankAmountMinor] = useState('');
  const [note, setNote] = useState('');

  const [selectedConfirmationId, setSelectedConfirmationId] = useState<string>('');
  const [selectedPayoutIds, setSelectedPayoutIds] = useState<Record<string, boolean>>({});
  const globalAuthorizedFetch = useMemo(
    () =>
      (input: RequestInfo | URL, init: RequestInit = {}) => {
        const headers = new Headers(init.headers || {});
        // Reconciliation page is intentionally global (not department-scoped).
        headers.set('x-department-slug', '');
        return authorizedFetch(input, { ...init, headers });
      },
    [authorizedFetch],
  );
  const selectedStripePayoutIds = useMemo(
    () => Object.entries(selectedPayoutIds).filter(([, v]) => v).map(([k]) => k),
    [selectedPayoutIds],
  );
  const selectedPayoutAmountMinor = useMemo(() => {
    const selected = new Set(selectedStripePayoutIds);
    return payouts
      .filter((p) => selected.has(p.stripePayoutId))
      .reduce((sum, p) => sum + p.amountMinor, 0);
  }, [payouts, selectedStripePayoutIds]);
  const unlinkedTotalMinor = useMemo(
    () => payouts.reduce((sum, p) => sum + p.amountMinor, 0),
    [payouts],
  );

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [pRes, cRes] = await Promise.all([
        globalAuthorizedFetch(`${API_BASE}/finance/stripe/payouts?unlinkedOnly=true`),
        globalAuthorizedFetch(`${API_BASE}/finance/bank-confirmations`),
      ]);
      if (!pRes.ok) throw new Error('payouts');
      if (!cRes.ok) throw new Error('confirmations');
      const pData = (await pRes.json()) as StripePayout[];
      const cData = (await cRes.json()) as BankConfirmation[];
      setPayouts(pData);
      setConfirmations(cData);
      if (!selectedConfirmationId && cData[0]?.id) setSelectedConfirmationId(cData[0].id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSync = async () => {
    setSyncing(true);
    setError(null);
    setNotice(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/finance/stripe/sync-payouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit: 100 }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any)?.message || 'Sync failed');
      }
      const body = (await res.json()) as { upserted?: number };
      setNotice(`Synced payouts: ${Number(body.upserted ?? 0)} updated from Stripe.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const onCreateConfirmation = async () => {
    setCreating(true);
    setError(null);
    setNotice(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/finance/bank-confirmations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currency,
          periodStart,
          periodEnd,
          bankAmountMinor: Number(bankAmountMinor || '0'),
          note: note.trim() ? note.trim() : undefined,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any)?.message || 'Create failed');
      }
      setNotice('Bank confirmation recorded.');
      setBankAmountMinor('');
      setNote('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const onLink = async () => {
    if (!selectedConfirmationId || selectedStripePayoutIds.length === 0) return;
    setError(null);
    setNotice(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/finance/bank-confirmations/link-payouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          confirmationId: selectedConfirmationId,
          stripePayoutIds: selectedStripePayoutIds,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any)?.message || 'Link failed');
      }
      const body = (await res.json()) as { linked?: number };
      setNotice(`Linked payouts: ${Number(body.linked ?? 0)}.`);
      setSelectedPayoutIds({});
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Link failed');
    }
  };

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath="/workspace/finance/reconciliation">
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {t('backoffice.bank_reconciliation_title', 'Bank reconciliation')}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {t('backoffice.bank_reconciliation_subtitle', 'Confirm bank deposits and link them to payouts and orders.')}
          </p>
        </section>

        {error ? (
          <div className="rounded-none border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        ) : null}
        {notice ? (
          <div className="rounded-none border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{notice}</div>
        ) : null}

        <section className="workspace-panel">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-slate-900">
              {t('reconciliation_snapshot', { defaultValue: 'Reconciliation snapshot' })}
            </h2>
            <span className="rounded-full bg-slate-900/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700">
              {confirmations.length} confirmations · {payouts.length} unlinked payouts
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="workspace-stat-card">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {t('unlinked_payout_value', { defaultValue: 'Unlinked payout value' })}
              </div>
              <div className="mt-1 text-xl font-semibold text-amber-700">
                {formatMajorInCurrency(
                  unlinkedTotalMinor / 100,
                  payouts[0]?.currency || 'USD',
                )}
              </div>
              <div className="mt-1 text-xs text-slate-600">
                {t('needs_confirmation_linking', {
                  defaultValue: 'Needs confirmation linking',
                })}
              </div>
            </div>
            <div className="workspace-stat-card">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {t('selected_payouts', { defaultValue: 'Selected payouts' })}
              </div>
              <div className="mt-1 text-xl font-semibold text-slate-900">{selectedStripePayoutIds.length}</div>
              <div className="mt-1 text-xs text-slate-600">
                {formatMajorInCurrency(
                  selectedPayoutAmountMinor / 100,
                  payouts[0]?.currency || 'USD',
                )}
              </div>
            </div>
            <div className="workspace-stat-card">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {t('latest_confirmation', { defaultValue: 'Latest confirmation' })}
              </div>
              <div className="mt-1 text-sm font-semibold text-slate-900">
                {confirmations[0] ? new Date(confirmations[0].confirmedAt).toLocaleString() : '—'}
              </div>
              <div className="mt-1 text-xs text-slate-600">
                {confirmations[0]
                  ? formatMajorInCurrency(
                      confirmations[0].bankAmountMinor / 100,
                      confirmations[0].currency,
                    )
                  : 'No confirmations yet'}
              </div>
            </div>
          </div>
        </section>

        <section className="workspace-panel space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-semibold text-slate-900">
              {t('stripe_payouts', { defaultValue: 'Stripe payouts' })}
            </h2>
            <button
              type="button"
              className={primaryBtnClass}
              onClick={onSync}
              disabled={syncing}
            >
              {syncing
                ? t('syncing', { defaultValue: 'Syncing…' })
                : t('sync_payouts', { defaultValue: 'Sync payouts' })}
            </button>
          </div>

          {loading ? (
            <p className="text-sm text-slate-600">{t('common.loading')}</p>
          ) : payouts.length === 0 ? (
            <p className="text-sm text-slate-600">
              {t('no_unlinked_payouts_found', { defaultValue: 'No unlinked payouts found.' })}
            </p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <table className="w-full table-auto text-left text-sm">
                <thead className="bg-slate-50 text-xs text-slate-600">
                  <tr>
                    <th className="px-3 py-2">Select</th>
                    <th className="px-3 py-2">Payout</th>
                    <th className="px-3 py-2">Currency</th>
                    <th className="px-3 py-2">Amount</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Arrival</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {payouts.map((p) => (
                    <tr key={p.stripePayoutId} className="hover:bg-slate-50/60">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={!!selectedPayoutIds[p.stripePayoutId]}
                          onChange={(e) =>
                            setSelectedPayoutIds((s) => ({ ...s, [p.stripePayoutId]: e.target.checked }))
                          }
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{p.stripePayoutId}</td>
                      <td className="px-3 py-2">{p.currency}</td>
                      <td className="px-3 py-2">
                        {formatMajorInCurrency(p.amountMinor / 100, p.currency)}
                      </td>
                      <td className="px-3 py-2">{p.status}</td>
                      <td className="px-3 py-2">{p.arrivalAt ? new Date(p.arrivalAt).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="workspace-panel space-y-3">
          <h2 className="text-base font-semibold text-slate-900">Bank confirmations</h2>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Currency</label>
              <input className={inputClass} value={currency} onChange={(e) => setCurrency(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Period start</label>
              <input className={inputClass} type="datetime-local" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Period end</label>
              <input className={inputClass} type="datetime-local" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-700">Bank amount (minor)</label>
              <input className={inputClass} inputMode="numeric" value={bankAmountMinor} onChange={(e) => setBankAmountMinor(e.target.value)} />
            </div>
            <div className="flex items-end">
              <button type="button" className={`${primaryBtnClass} w-full`} onClick={onCreateConfirmation} disabled={creating}>
                {creating ? 'Saving…' : 'Confirm deposit'}
              </button>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700">Note</label>
            <input className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white">
              <div className="border-b border-slate-100 p-3 text-sm font-semibold text-slate-900">Recent confirmations</div>
              <div className="max-h-64 overflow-auto">
                {confirmations.length === 0 ? (
                  <p className="p-3 text-sm text-slate-600">No confirmations yet.</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {confirmations.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 ${selectedConfirmationId === c.id ? 'bg-slate-50' : ''}`}
                          onClick={() => setSelectedConfirmationId(c.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="font-medium text-slate-900">
                              {formatMajorInCurrency(c.bankAmountMinor / 100, c.currency)}
                            </div>
                            <div className="text-xs text-slate-600">{new Date(c.confirmedAt).toLocaleString()}</div>
                          </div>
                          <div className="mt-1 text-xs text-slate-600">
                            {new Date(c.periodStart).toLocaleString()} → {new Date(c.periodEnd).toLocaleString()} · {c.payoutLinks?.length ?? 0} payouts
                          </div>
                          {c.note ? <div className="mt-1 text-xs text-slate-600">{c.note}</div> : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-100 p-3">
                <div className="text-sm font-semibold text-slate-900">Link selected payouts</div>
                <button
                  type="button"
                  className={primaryBtnClass}
                  onClick={onLink}
                  disabled={!selectedConfirmationId || selectedStripePayoutIds.length === 0}
                >
                  Link
                </button>
              </div>
              <div className="p-3 text-sm text-slate-700">
                <div>
                  <span className="font-medium">Confirmation:</span>{' '}
                  {selectedConfirmationId ? selectedConfirmationId : 'None selected'}
                </div>
                <div className="mt-1">
                  <span className="font-medium">Selected payouts:</span> {selectedStripePayoutIds.length}
                </div>
                {selectedStripePayoutIds.length ? (
                  <ul className="mt-2 max-h-[280px] list-disc overflow-auto pl-5 text-xs text-slate-600">
                    {selectedStripePayoutIds.map((id) => (
                      <li key={id} className="font-mono">
                        {id}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      </main>
    </MainLayout>
  );
}

